// frontend/src/pages/ResearchPage.tsx
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen,
  Search,
  Download,
  FileText,
  Filter,
  Loader2,
  AlertTriangle,
  TagIcon,
} from "lucide-react";

import {
  type Research as ResearchListedType,
  type TagPublic,
  type FetchResearchParams,
} from "@/types";
import { getResearchItems } from "@/api/researchService";
import { getAllTags } from "@/api/tagService";
import { Label } from "@/components/ui/label";
import { useSiteSettingsStore } from "@/store/settingsStore";

const INITIAL_VISIBLE_FILTER_TAGS = 7;

const ResearchPage = () => {
  const [researchItems, setResearchItems] = useState<ResearchListedType[]>([]);
  const [allTags, setAllTags] = useState<TagPublic[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const [selectedTagNames, setSelectedTagNames] = useState<string[]>(
    searchParams.getAll("tags") || []
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTags, setIsLoadingTags] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllFilterTags, setShowAllFilterTags] = useState(false);

  // State to explicitly trigger a refetch of all page data.
  // This is crucial for the "Try Again" functionality.
  const [fetchAttempt, setFetchAttempt] = useState(0);

  const publicSettings = useSiteSettingsStore((state) => state.publicSettings);
  const isLoadingSettings = useSiteSettingsStore((state) => state.isLoading);

  const pageTitle = isLoadingSettings
    ? "Loading..."
    : publicSettings?.research_page_header_title || "Vedas Research Library";
  const pageSubtitle = isLoadingSettings
    ? "Loading description..."
    : publicSettings?.research_page_header_subtitle ||
      "Access our collection of in-depth research papers, market analysis, and whitepapers on key financial topics and emerging trends.";

  const backendBaseUrl =
    import.meta.env.VITE_API_BASE_URL?.replace("/api/v1", "") ||
    "http://localhost:8000";

  const getFullAssetUrl = useCallback(
    (assetPath?: string | null): string => {
      if (!assetPath) return "#";
      if (assetPath.startsWith("http://") || assetPath.startsWith("https://")) {
        return assetPath;
      }
      const cleanBaseUrl = backendBaseUrl.endsWith("/")
        ? backendBaseUrl.slice(0, -1)
        : backendBaseUrl;
      const staticPath = assetPath.startsWith("/")
        ? assetPath
        : `/${assetPath}`;
      return `${cleanBaseUrl}/static${staticPath.replace("/static", "")}`;
    },
    [backendBaseUrl]
  );

  // Effect for fetching the filter tags.
  // It runs on the initial render and whenever a retry is explicitly triggered.
  useEffect(() => {
    const fetchTags = async () => {
      setIsLoadingTags(true);
      try {
        const tagsData = await getAllTags();
        setAllTags(tagsData && tagsData.length > 0 ? tagsData : []);
      } catch (err) {
        console.error(
          "Failed to fetch tags. Filters may not be available.",
          err
        );
        setAllTags([]); // Ensure tags are empty on failure
      } finally {
        setIsLoadingTags(false);
      }
    };
    fetchTags();
  }, [fetchAttempt]); // Dependency on fetchAttempt allows for manual retries.

  // Effect for fetching the research items.
  // This is the main data fetching effect for the page's content.
  useEffect(() => {
    // Guard clause: Do not attempt to fetch research items until the tags
    // have been loaded (or the attempt has failed). This prevents the initial
    // double-fetch race condition and ensures a sequential data load.
    if (isLoadingTags) {
      return;
    }

    const fetchResearch = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const apiParams: FetchResearchParams = {
          status: "published",
        };

        if (selectedTagNames.length > 0) {
          apiParams.tags = selectedTagNames;
        }

        if (searchTerm.trim()) {
          apiParams.search = searchTerm.trim();
        }

        const fetchedItemsFromApi = await getResearchItems(apiParams);
        // Ensure we always work with an array, even if the API returns something else.
        let processedItems = Array.isArray(fetchedItemsFromApi)
          ? fetchedItemsFromApi
          : [];

        // Note: The backend should ideally handle all filtering.
        // These client-side filters are kept as a fallback or for refinement.
        if (searchTerm.trim()) {
          const lowerSearchTerm = searchTerm.trim().toLowerCase();
          processedItems = processedItems.filter(
            (item) =>
              item.title.toLowerCase().includes(lowerSearchTerm) ||
              (item.description &&
                item.description.toLowerCase().includes(lowerSearchTerm))
          );
        }

        if (selectedTagNames.length > 0) {
          processedItems = processedItems.filter(
            (item) =>
              item.tags &&
              selectedTagNames.every((selectedTagName) =>
                item.tags.some(
                  (itemTag) =>
                    itemTag.name.toLowerCase() === selectedTagName.toLowerCase()
                )
              )
          );
        }
        setResearchItems(processedItems);

        // Update URL search params to reflect the current state of filters.
        // Using the functional update form of setSearchParams to avoid needing
        // searchParams as a dependency, which would cause a re-render loop.
        setSearchParams(
          (prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            if (searchTerm.trim()) {
              newParams.set("search", searchTerm.trim());
            } else {
              newParams.delete("search");
            }
            newParams.delete("tags");
            selectedTagNames.forEach((tag) => newParams.append("tags", tag));
            return newParams;
          },
          { replace: true }
        );
      } catch (err: any) {
        console.error("Failed to fetch research items:", err);
        let errorMessage =
          "Could not load research papers. Please try again later.";
        if (err.response?.data?.detail) {
          errorMessage = `Error: ${err.response.data.detail}`;
        } else if (err.message) {
          errorMessage = err.message;
        }
        setError(errorMessage);
        setResearchItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimeout = setTimeout(fetchResearch, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, selectedTagNames, isLoadingTags, fetchAttempt]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const toggleTag = (tagName: string) => {
    setSelectedTagNames((prev) =>
      prev.includes(tagName)
        ? prev.filter((t) => t !== tagName)
        : [...prev, tagName]
    );
  };

  // The "Try Again" function now clears state and triggers a full data refetch.
  const handleTryAgain = () => {
    setError(null);
    setSearchTerm("");
    setSelectedTagNames([]);
    setSearchParams({}, { replace: true });
    setFetchAttempt((prev) => prev + 1);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Initial loading state UI
  if (isLoading && researchItems.length === 0 && !error) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">
          Loading Research Papers...
        </p>
      </div>
    );
  }

  // Error state UI, now with a fully functional "Try Again" button
  if (error && researchItems.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center min-h-[60vh] flex flex-col justify-center items-center">
        <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-6" />
        <h2 className="text-2xl font-semibold text-destructive mb-2">
          Error Loading Research
        </h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button onClick={handleTryAgain} variant="outline">
          Try Again
        </Button>
      </div>
    );
  }

  const visibleFilterTags = showAllFilterTags
    ? allTags
    : allTags.slice(0, INITIAL_VISIBLE_FILTER_TAGS);

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 text-foreground bg-background">
      <header className="mb-10 md:mb-16 text-center border-b border-border/60 pb-10 relative">
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2/3 md:w-1/3 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent"></div>
        <BookOpen className="h-16 w-16 text-primary mx-auto mb-4" />
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
          <span
            dangerouslySetInnerHTML={{
              __html:
                pageTitle.replace(
                  /Research/g,
                  '<span class="text-gradient-maroon-to-gold">Research</span>'
                ) ||
                'Vedas <span class="text-gradient-maroon-to-gold">Research</span> Library',
            }}
          />
        </h1>
        <p className="mt-4 text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
          {pageSubtitle}
        </p>
      </header>

      <section className="mb-10 p-6 bg-muted/70 dark:bg-muted/20 rounded-lg shadow-md">
        <div className="grid md:grid-cols-3 gap-6 items-start">
          <div className="md:col-span-2">
            <Label
              htmlFor="search-research"
              className="block text-sm font-medium text-foreground mb-1"
            >
              Search Research
            </Label>
            <div className="relative">
              <Input
                id="search-research"
                type="text"
                placeholder="Search by title or keyword..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 border-border focus:border-primary focus:ring-primary"
              />
              <Search className="absolute left-3 top-1/2 h-5 w-5 text-muted-foreground -translate-y-1/2" />
            </div>
          </div>
          <div>
            <Label className="block text-sm font-medium text-foreground mb-1">
              <Filter className="inline h-4 w-4 mr-1.5 text-primary/80" />{" "}
              Filter by Tags
            </Label>
            <div className="flex flex-wrap gap-2 pt-2 min-h-[38px]">
              <TagIcon className="h-5 w-5 text-muted-foreground mr-1" />
              {isLoadingTags ? (
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              ) : visibleFilterTags.length > 0 ? (
                visibleFilterTags.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant={
                      selectedTagNames.includes(tag.name)
                        ? "default"
                        : "outline"
                    }
                    onClick={() => toggleTag(tag.name)}
                    className="cursor-pointer transition-colors data-[state=selected]:bg-primary data-[state=selected]:text-primary-foreground data-[state=selected]:hover:bg-primary/90 hover:bg-accent/10 dark:hover:bg-accent/20"
                  >
                    {tag.name}
                  </Badge>
                ))
              ) : (
                <span className="text-xs text-muted-foreground">
                  No tags available
                </span>
              )}
              {!isLoadingTags &&
                allTags.length > INITIAL_VISIBLE_FILTER_TAGS && (
                  <Badge
                    variant="secondary"
                    onClick={() => setShowAllFilterTags(!showAllFilterTags)}
                    className="cursor-pointer hover:bg-accent/20"
                  >
                    {showAllFilterTags ? "Show Less..." : "More..."}
                  </Badge>
                )}
            </div>
          </div>
        </div>
      </section>

      {/* Loading overlay for subsequent fetches */}
      {isLoading && researchItems.length > 0 && (
        <div className="relative">
          <div className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-20 rounded-md">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
          <div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 opacity-50"
            aria-hidden="true"
          >
            {researchItems.map((item) => (
              <Card
                key={`${item.id}-loading`}
                className="group flex flex-col bg-card text-card-foreground overflow-hidden shadow-lg"
              >
                <CardHeader className="pb-3 pt-5">
                  <CardTitle className="text-xl truncate">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow py-2">
                  <p className="text-sm text-muted-foreground line-clamp-4">
                    {item.description}
                  </p>
                </CardContent>
                <CardFooter className="p-4 pt-3">
                  <div className="h-10 w-full bg-muted rounded"></div>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Display research items */}
      {!isLoading && researchItems.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {researchItems.map((item) => (
            <Card
              key={item.id}
              className="group flex flex-col bg-card text-card-foreground overflow-hidden shadow-lg hover:shadow-xl
                                            dark:hover:shadow-primary/10 transition-all duration-300 ease-in-out transform hover:-translate-y-1.5 border border-border/50
                                            hover:border-primary/40 dark:border-border dark:hover:border-primary/30 relative"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent/90 via-primary/90 to-accent/90 scale-x-0 group-hover:scale-x-100 transition-transform duration-400 ease-out origin-center"></div>
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400
                                            bg-gradient-to-br from-primary/5 via-background to-accent/5 dark:from-primary/10 dark:via-card dark:to-accent/10"
              ></div>
              <div className="relative z-10 flex flex-col flex-grow h-full">
                <CardHeader className="pb-3 pt-5">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-xl mb-1 pr-2 text-foreground group-hover:text-primary dark:group-hover:text-accent transition-colors">
                      <a
                        href={getFullAssetUrl(item.pdf_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {item.title}
                      </a>
                    </CardTitle>
                    <FileText className="h-8 w-8 text-primary/70 group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
                  </div>
                  <CardDescription className="text-xs text-muted-foreground pt-1">
                    Published on{" "}
                    {formatDate(item.published_at || item.upload_date)}
                    {item.uploader_name && ` by ${item.uploader_name}`}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow py-2">
                  <p className="text-sm text-muted-foreground line-clamp-4 group-hover:text-foreground/90 transition-colors">
                    {item.description || "No description available."}
                  </p>
                </CardContent>
                <CardFooter className="bg-transparent group-hover:bg-muted/20 dark:group-hover:bg-muted/10 p-4 pt-3 flex-col items-start space-y-3 transition-colors duration-300">
                  <div className="flex flex-wrap gap-1.5 min-h-[22px]">
                    {item.tags &&
                      item.tags.slice(0, 3).map((tag) => (
                        <Badge
                          key={tag.id}
                          variant="secondary"
                          className="text-xs px-2 py-0.5"
                        >
                          {tag.name}
                        </Badge>
                      ))}
                    {item.tags && item.tags.length > 3 && (
                      <Badge
                        variant="secondary"
                        className="text-xs px-2 py-0.5"
                      >
                        +{item.tags.length - 3} more
                      </Badge>
                    )}
                  </div>
                  <Button
                    asChild
                    variant="default"
                    className="w-full !bg-gradient-to-r !from-primary !to-vedasMaroon-700 hover:!from-primary/90 hover:!to-vedasMaroon-700/90 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-300"
                  >
                    <a
                      href={getFullAssetUrl(item.pdf_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Download className="mr-2 h-4 w-4" /> View / Download PDF
                    </a>
                  </Button>
                </CardFooter>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* No results message */}
      {!isLoading && researchItems.length === 0 && !error && (
        <div className="text-center py-16">
          <Search className="h-20 w-20 text-muted-foreground/50 mx-auto mb-6" />
          <h3 className="text-2xl font-semibold text-foreground mb-2">
            No Research Papers Found
          </h3>
          <p className="text-muted-foreground">
            Try adjusting your search term or filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default ResearchPage;
